import { useState, useEffect, useCallback } from "react";

import useClientNotes from "../hooks/useClientNotes";
import { STRINGS } from "../utils/strings";

import { useNotesIndexDB } from "./useNotesIndexDB";

const normalize = (str) =>
  str
    ?.replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "";

export const useNoteEditor = ({
  mode,
  data,
  userId,
  handleSubmit,
  hideNoteTypeSelector = false,
}) => {
  const [selectedNoteType, setSelectedNoteType] = useState(true);
  const [selectedNoteContent, setSelectedNoteContent] = useState("");
  const [requestObject, setRequestObject] = useState(null);
  const [initialData, setInitialData] = useState(null);

  const { removeTags } = useClientNotes();

  const { initializeDraft, autoSaveDraft, clearDrafts } = useNotesIndexDB({
    userId,
    noteId: data?.id,
    initialContent: data?.content ?? "",
  });

  const initializeModal = useCallback(() => {
    const isEdit = mode === STRINGS.EDIT_MODE;
    const noteType = hideNoteTypeSelector ? true : isEdit ? data?.internalOnly : false;

    initializeDraft().then((content) => {
      const initial = { internalOnly: noteType, content };
      setSelectedNoteType(noteType);
      setSelectedNoteContent(content);
      setInitialData(initial);
      setRequestObject(initial);
    });
  }, [data, hideNoteTypeSelector, initializeDraft, mode]);

  useEffect(() => {
    autoSaveDraft(selectedNoteContent);
  }, [selectedNoteContent, autoSaveDraft]);

  const reset = async () => {
    await clearDrafts();
    initializeModal();
    if (initialData && mode === STRINGS.EDIT_MODE && removeTags(normalize(initialData?.content))) {
      setSelectedNoteType(initialData.internalOnly);
      setSelectedNoteContent(initialData.content);
      setRequestObject(initialData);
    } else {
      const defaultData = { internalOnly: hideNoteTypeSelector ? true : false, content: "" };
      setSelectedNoteType(hideNoteTypeSelector ? true : false);
      setSelectedNoteContent("");
      setRequestObject(defaultData);
    }
  };

  const hasContent = () => normalize(selectedNoteContent) !== "";

  const disableButtons = () => {
    if (mode === STRINGS.ADD_MODE) {
      return !hasContent();
    }

    const currentContent = normalize(selectedNoteContent);
    const originalContent = normalize(data?.content || "");
    const currentType = selectedNoteType;
    const originalType = hideNoteTypeSelector ? true : (data?.internalOnly ?? true);

    return currentContent === originalContent && currentType === originalType;
  };

  const handleSave = () => {
    clearDrafts();
    handleSubmit(data?.id, requestObject);
  };

  const handleTypeChange = (type) => {
    if (hideNoteTypeSelector) {
      return;
    }
    setSelectedNoteType(type);
    initializeDraft().then((content) => {
      setSelectedNoteContent(content);
      setRequestObject({ internalOnly: type, content });
    });
  };

  const handleContentChange = (text) => {
    setSelectedNoteContent(text);
    setRequestObject({
      ...(requestObject ?? { internalOnly: hideNoteTypeSelector ? true : selectedNoteType }),
      internalOnly: hideNoteTypeSelector ? true : selectedNoteType,
      content: text,
    });
  };

  return {
    selectedNoteType,
    selectedNoteContent,
    disableButtons,
    reset,
    handleSave,
    handleTypeChange,
    handleContentChange,
    initializeModal,
  };
};
